import { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI } from '../../utils/api';
import { useToast, apiError, useConfirm } from '../../components/Toast';
import { useAuth } from '../../hooks/useAuth';
import { getPermLevel } from '../../utils/rolesConfig';
import { EmptyState } from '../../components/layout/UI';
import { useSelection, SelectAll, RowCheck, TypeToConfirm } from '../../components/BulkActions';
import { format, formatDistanceToNow } from 'date-fns';
import { Trash2, RotateCcw, ShieldCheck, X } from 'lucide-react';

/**
 * frontend/src/pages/admin/AdminRecycleBinPage.jsx
 *
 * Its own page, not a panel inside Data Management.
 *
 * The first version put it there because that page already owned permanent
 * deletion. But Data Management is SystemAdmin-only, and restore is an Admin
 * action — so an Admin was allowed by the API to restore something and could
 * never reach the button. The permission and the placement disagreed.
 *
 * Here: Admin can open the page and restore. Permanent deletion is still
 * SystemAdmin, enforced by the API, and the button is hidden for anyone else
 * rather than failing when pressed.
 */

const SAPPHIRE = '#2E539F';
const DANGER   = '#ef4444';

export default function AdminRecycleBinPage() {
  const toast   = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);
  const [filter, setFilter]   = useState('');
  const [pending, setPending] = useState(null);   // 'restore' | 'purge'
  const [working, setWorking] = useState(false);

  const canPurge = getPermLevel(user?.role) === 'SystemAdmin';

  const PAGE = 200;
  const [total, setTotal] = useState(0);

  /**
   * Clearing an entity can put thousands of rows in here at once, so the page
   * asks for a screen at a time and says how many it is not showing. A page
   * that silently showed the first 200 of 4,000 would look finished.
   */
  const load = useCallback(async (append = false) => {
    setLoading(!append);
    try {
      const offset = append ? rowsRef.current.length : 0;
      const r = await adminAPI.getRecycleBin({ limit: PAGE, offset });
      const batch = r.data || [];
      setTotal(parseInt(r.headers?.['x-total-count'], 10) || batch.length);
      setRows(prev => (append ? [...prev, ...batch] : batch));
    } catch (e) {
      toast.error(apiError(e, 'Could not load the recycle bin.'));
    } finally { setLoading(false); }
  }, [toast]);

  // load() reads the current length without listing rows as a dependency,
  // which would rebuild it on every append and re-run the effect forever.
  const rowsRef = useRef([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  useEffect(() => { load(); }, [load]);

  const restore = async (row) => {
    setBusy(row.id);
    try {
      await adminAPI.restoreRecord(row.entity, row.id);
      toast.success(`${row.label} restored`);
      load();
    } catch (e) { toast.error(apiError(e, 'Could not restore it.')); }
    finally { setBusy(null); }
  };

  const purge = async (row) => {
    const ok = await confirm(
      `Permanently delete "${row.name}"? This is the one action in the system with no undo.`,
      { title: 'Delete permanently', confirmLabel: 'Delete permanently', danger: true });
    if (!ok) return;
    setBusy(row.id);
    try {
      await adminAPI.purgeRecord(row.entity, row.id);
      toast.success(`${row.label} permanently deleted`);
      load();
    } catch (e) { toast.error(apiError(e, 'Could not delete it.')); }
    finally { setBusy(null); }
  };

  const visible = filter ? rows.filter(r => r.entity === filter) : rows;
  const kinds = [...new Set(rows.map(r => r.entity))];

  // The bin mixes record types, so a row is identified by both. useSelection
  // works on `id`, so the rows it sees carry a composite one.
  const keyed = visible.map(r => ({ ...r, id: `${r.entity}:${r.id}` }));
  const sel = useSelection(keyed);
  const items = sel.selected.map(k => {
    const [entity, ...rest] = k.split(':');
    return { entity, id: rest.join(':') };
  });

  const runBulk = async () => {
    setWorking(true);
    try {
      const r = pending === 'restore'
        ? await adminAPI.bulkRestoreRecords(items)
        : await adminAPI.bulkPurgeRecords(items);
      toast.success(r.data?.message || 'Done');
      setPending(null);
      sel.clear();
      load();
    } catch (e) {
      toast.error(apiError(e, 'Nothing was changed.'));
    } finally { setWorking(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="page-title">Recycle Bin</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
          Records people have removed from their own Deleted tab. They are hidden
          everywhere in the application but not yet gone.
        </p>
      </div>

      {rows.length > 0 && kinds.length > 1 && (
        <div className="flex gap-2">
          <button onClick={() => setFilter('')}
            className={`text-sm px-3 py-1.5 rounded-lg border ${!filter ? 'font-semibold' : ''}`}
            style={{ borderColor: 'var(--border)',
                     color: !filter ? SAPPHIRE : 'var(--text-secondary)' }}>
            All ({rows.length})
          </button>
          {kinds.map(k => {
            const n = rows.filter(r => r.entity === k).length;
            const label = rows.find(r => r.entity === k)?.label || k;
            return (
              <button key={k} onClick={() => setFilter(k)}
                className={`text-sm px-3 py-1.5 rounded-lg border ${filter === k ? 'font-semibold' : ''}`}
                style={{ borderColor: 'var(--border)',
                         color: filter === k ? SAPPHIRE : 'var(--text-secondary)' }}>
                {label}s ({n})
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Nothing deleted"
          message="Nothing has reached this stage. An item appears here once someone deletes it a second time, from their own Deleted tab." />
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background:'var(--surface-alt)' }}>
                <th className="px-4 py-2.5 w-10">
                  <SelectAll allChecked={sel.allChecked} someChecked={sel.someChecked}
                    onToggle={sel.toggleAll} />
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
                  style={{ color:'var(--text-secondary)' }}>Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
                  style={{ color:'var(--text-secondary)' }}>Name</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
                  style={{ color:'var(--text-secondary)' }}>Deleted</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
                  style={{ color:'var(--text-secondary)' }}>By</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
                  style={{ color:'var(--text-secondary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor:'var(--border)' }}>
              {visible.map(row => (
                <tr key={`${row.entity}-${row.id}`}
                  style={sel.isSelected(`${row.entity}:${row.id}`)
                    ? { background:'rgba(46,83,159,.06)' } : undefined}>
                  <td className="px-4 py-2.5">
                    <RowCheck checked={sel.isSelected(`${row.entity}:${row.id}`)}
                      onToggle={() => sel.toggle(`${row.entity}:${row.id}`)}
                      label={row.name || row.label} />
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
                      {row.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 max-w-xs truncate" style={{ color:'var(--text)' }}
                    title={row.name || ''}>
                    {row.name || '(no name)'}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>
                    {row.deleted_at
                      ? <span title={format(new Date(row.deleted_at), 'd MMM yyyy HH:mm')}>
                          {formatDistanceToNow(new Date(row.deleted_at), { addSuffix: true })}
                        </span>
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>
                    {row.deleted_by_name || '—'}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => restore(row)} disabled={busy === row.id}
                        className="btn-secondary text-xs flex items-center gap-1.5">
                        <RotateCcw size={13} /> Restore
                      </button>
                      {/* Hidden rather than shown-and-refused. A button that
                          always fails is worse than no button. */}
                      {canPurge && (
                        <button onClick={() => purge(row)} disabled={busy === row.id}
                          className="text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5"
                          style={{ borderColor: 'var(--border)', color: DANGER }}>
                          <Trash2 size={13} /> Delete permanently
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rows.length < total && (
        <div className="flex items-center justify-center gap-3">
          <span className="text-sm" style={{ color:'var(--text-secondary)' }}>
            Showing {rows.length.toLocaleString()} of {total.toLocaleString()}
          </span>
          <button onClick={() => load(true)} className="btn-secondary text-sm">
            Load {Math.min(PAGE, total - rows.length)} more
          </button>
        </div>
      )}

      {sel.count > 0 && (
        <div className="fixed left-1/2 -translate-x-1/2 z-[150] flex items-center gap-2 px-3 py-2 rounded-xl shadow-lg"
          style={{ bottom: 20, background:'var(--surface)', border:'1px solid var(--border)',
                   borderTop:`3px solid ${SAPPHIRE}` }}>
          <span className="text-sm font-semibold px-1" style={{ color:'var(--text)' }}>
            {sel.count} selected
          </span>
          <button onClick={() => setPending('restore')}
            className="text-sm px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5"
            style={{ background:'rgba(46,83,159,.10)', color:SAPPHIRE }}>
            <RotateCcw size={14} /> Restore
          </button>
          {canPurge && (
            <button onClick={() => setPending('purge')}
              className="text-sm px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5"
              style={{ background:'rgba(239,68,68,.10)', color:DANGER }}>
              <Trash2 size={14} /> Delete permanently
            </button>
          )}
          <button onClick={sel.clear} aria-label="Clear the selection"
            className="p-1.5 rounded-lg" style={{ color:'var(--text-secondary)' }}>
            <X size={15} />
          </button>
        </div>
      )}

      <TypeToConfirm
        open={pending === 'restore'} word="" danger={false}
        title={`Restore ${sel.count} record${sel.count === 1 ? '' : 's'}?`}
        body="They come back to the lists where they were. A restore brings back the record itself, not whatever was deleted alongside it."
        confirmLabel="Restore" busy={working}
        onConfirm={runBulk} onCancel={() => setPending(null)} />

      <TypeToConfirm
        open={pending === 'purge'} word="DELETE"
        title={`Permanently delete ${sel.count} record${sel.count === 1 ? '' : 's'}?`}
        body="This is the one action in the system with no undo. The rows are removed from the database."
        lines={[`${sel.count} record${sel.count === 1 ? '' : 's'} will be removed.`,
                'Nothing live can be touched by this, whatever is selected.']}
        confirmLabel="Delete permanently" busy={working}
        onConfirm={runBulk} onCancel={() => setPending(null)} />

      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
        Tick rows to act on many at once. Anyone with admin access can restore.
        {canPurge
          ? ' Permanent deletion is System Administrator only, which you are.'
          : ' Permanent deletion is System Administrator only.'}
        {' '}Items are also swept automatically by the purge in Data Management.
      </p>
    </div>
  );
}
